import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import {
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
  PreloadAllModules,
  TitleStrategy,
} from '@angular/router';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '@env/environment';
import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { AppErrorHandler } from '@gfc-flash/service-error';
import {
  HttpBackend,
  provideHttpClient,
  withInterceptors,
  withXsrfConfiguration,
} from '@angular/common/http';
import {
  translateHttpLoaderProvider,
  TranslateModule,
} from '@gfc-flash/service-translate';
import {
  IdleKeepAliveModule,
  provideIdle,
  provideIdleKeepAlive,
} from '@gfc-flash/service-idle';
import { GlobalServicesInitializerService } from '@core/services/global-services-initializer/global-services-initializer.service';
import { TranslatedTitleStrategy } from '@core/strategies/translate-title/translate-title.strategy';
import { CodeIso6391Enum } from '@gfc-flash/package-language';
import { GlobalEventsInitializerService } from '@core/services/global-events-initializer/global-events-initializer.service';
import {
  AuthentificationInterceptor,
  CapacitorHttpBackendHandler,
} from '@gfc-flash/interceptor-authentification';
import { HttpErrorInterceptor } from '@gfc-flash/interceptor-http-error';
import { GlobalPagesInitializerService } from '@core/services/global-pages-initializer/global-pages-initializer.service';
import {
  AuthorizationCookieEnum,
  AuthorizationHeaderEnum,
} from '@gfc-flash/package-authentification';

bootstrapApplication(AppComponent, {
  providers: [
    provideAppInitializer(async () => {
      const globalServicesInitializerService: GlobalServicesInitializerService =
        inject(GlobalServicesInitializerService);
      const globalEventsInitializerService: GlobalEventsInitializerService =
        inject(GlobalEventsInitializerService);
      const globalPagesInitializerService: GlobalPagesInitializerService =
        inject(GlobalPagesInitializerService);
      await globalServicesInitializerService.initializeServices();
      await globalEventsInitializerService.initializeEvents();
      await globalPagesInitializerService.initializePages();
    }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      experimentalCloseWatcher: true,
    }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideHttpClient(
      withInterceptors([AuthentificationInterceptor, HttpErrorInterceptor]),
      withXsrfConfiguration({
        cookieName: AuthorizationCookieEnum.CSRF_TOKEN,
        headerName: AuthorizationHeaderEnum.CSRF_TOKEN,
      }),
    ),
    provideIdle(),
    provideIdleKeepAlive(),
    importProvidersFrom([
      BrowserModule,
      TranslateModule.forRoot({
        loader: translateHttpLoaderProvider({
          prefix: '/i18n/',
          suffix: '.json',
          fallbackFiles: {
            [CodeIso6391Enum.EN_US]: 'en',
            [CodeIso6391Enum.FR_FR]: 'fr',
          },
          showLog: true,
        }),
        fallbackLang: CodeIso6391Enum.EN_US,
        lang: CodeIso6391Enum.FR_FR,
      }),
      IdleKeepAliveModule.forRoot(),
    ]),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    {
      provide: HttpBackend,
      useClass: CapacitorHttpBackendHandler,
    },
    {
      provide: ErrorHandler,
      useClass: AppErrorHandler,
    },
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
  ],
}).then();
